import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Typography, Radio } from "antd";
import {
  ArrowLeftOutlined, DownloadOutlined,
  SaveOutlined, PlusOutlined, DeleteOutlined,
} from "@ant-design/icons";
import { SbButton, SbSelect, SbInput, SbTable, SbModal, SbNotification, SbTag, SbTooltip } from "uiCore";
import { PageHeaderStyled, HeaderLeftStyled, HeadingRowStyled } from "styles/common.styles";
import {
  PageTransitionStyled, WorkspaceToolbarStyled, ToolbarActionsStyled,
  OrgTreeCardsRowStyled, RoleTableWrapperStyled, BackLinkStyled,
  ProposedRolesHeaderStyled, TreeDividerStyled,
  PdfCaptureStageStyled, OrgChartListStyled, TreeContainerStyled,
  ModalFieldStyled, FullWidthInputNumberStyled,
  SizedInputNumberStyled, TooltipTargetStyled,
} from "./orgDesign.styles";
import OrgTreeCard from "./components/OrgTreeCard";
import OrgDesignChartNode from "./components/OrgDesignChartNode";
import RoleChangeLegend from "./components/RoleChangeLegend";
import OrgDesignSummaryBridge from "./components/OrgDesignSummaryBridge";
import OrgDesignBudgetBar from "./components/OrgDesignBudgetBar";
import { useOrgDesignWorkspace, useCanViewOrgDesignBudget, useCanCreateOrgRedesign, useCanUpdateOrgRedesign } from "./useOrgDesignData";
import useOrgTreeViewportSync from "./useOrgTreeViewportSync";
import { getDescendantIds, newRoleNodeId } from "./orgDesignTreeEngine";
import { downloadOrgDesignPdf } from "./utils/orgDesignPdf";
import useAnalytics from "Hooks/useAnalytics";
import { ANALYTICS_EVENTS } from "Utils/analytics";
import { showSbConfirm } from "Utils/sbModal";
import useAuthStore from "Store/useAuthStore";
import { STATUS_LABELS, STATUS_COLORS, ENTRY_OPTION, getFilters, activatableRowProps, getRoleChangeConfig, ORG_DESIGN_WORKSPACE_FROM } from "./orgDesign.constants";

const { Title, Text } = Typography;

const VIEW_ORG = "org";
const VIEW_TABLE = "table";

const EVENTS = ANALYTICS_EVENTS.ORG_DESIGN;

// Waits two repaints — used so the off-screen PDF capture stage (mounted
// fresh on Download PDF; see `pdfExporting` below) has actually been laid
// out before html2canvas measures it. Same technique OrgTreeCard.jsx uses
// before its own PNG export/zoom-reset.
const nextPaint = () => new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

const OrgDesignWorkspace = () => {
  const navigate = useNavigate();
  const {
    draft, budgetSyncing, scheduleBudgetSync, updateHeadcount, updateReportingLine, renameRole,
    addRole, removeNewRole, updateNewRoleHeadcount, updateNewRoleManager,
    setTargetBudget, saveVersion,
  } = useOrgDesignWorkspace();
  const track = useAnalytics(ORG_DESIGN_WORKSPACE_FROM);
  const canViewBudget = useCanViewOrgDesignBudget();
  const canCreateOrgRedesign = useCanCreateOrgRedesign();
  const canUpdateOrgRedesign = useCanUpdateOrgRedesign();
  // "Prepared by" on the PDF report's header — falls back to email when the
  // profile has no display name set.
  const preparedBy = useAuthStore((s) => s.profileName || s.email);
  // The off-screen, fully expanded trees the PDF captures (see
  // PdfCaptureStageStyled below).
  const pdfCurrentTreeRef = useRef(null);
  const pdfProposedTreeRef = useRef(null);

  // Current Org starts with no proposed changes, so Table View is the more
  // useful first look; PAIC/opened versions already have a tree worth seeing.
  const [view, setView] = useState(() =>
    draft?.entryOption === ENTRY_OPTION.EXISTING_ORG ? VIEW_TABLE : VIEW_ORG
  );
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [saveLabel, setSaveLabel] = useState("");
  const [savingVersion, setSavingVersion] = useState(false);
  // Mounts the off-screen PDF capture stage only for the span of an actual
  // export, so a large org isn't rendered twice on every workspace visit.
  const [pdfExporting, setPdfExporting] = useState(false);
  const [addRoleOpen, setAddRoleOpen] = useState(false);
  const [newRoleDraft, setNewRoleDraft] = useState({ title: "", managerId: "", headcount: 1, microtaskFunction: "", annualCost: 140000 });
  // Shared zoom/scroll so the Current and Proposed OrgTreeCards pan and
  // zoom in lockstep — see useOrgTreeViewportSync. Renamed onto OrgTreeCard's
  // own controlled-prop names once here rather than at each of the three
  // call sites below.
  const { zoomIn, zoomOut, resetView, reportUserScroll, ...treeViewportRest } = useOrgTreeViewportSync();
  const treeViewport = {
    ...treeViewportRest,
    onZoomIn: zoomIn,
    onZoomOut: zoomOut,
    onResetView: resetView,
    onUserScroll: reportUserScroll,
  };

  // Memoized so every row's reparentOptionsFor() call reuses the same base
  // list instead of rebuilding it per row. Declared before `if (!draft)
  // return null` (uses draft? internally) so the hook always runs.
  const pseudoNodesForReparent = useMemo(
    () => [
      ...(draft?.roles || []).map((r) => ({ sb_unique_id: r.sb_unique_id, manager_id: r.manager_id })),
      ...(draft?.newRoles || []).map((nr) => ({ sb_unique_id: newRoleNodeId(nr.id), manager_id: nr.manager_id })),
    ],
    [draft?.roles, draft?.newRoles]
  );

  useEffect(() => {
    if (!draft) navigate("/org-design");
  }, [draft, navigate]);

  // One server budget sync on open so the Budget Bar/Role Count show real
  // figures immediately, not just after the first edit. `immediate: true`
  // skips the edit-debounce delay — there's no rapid edit burst to protect
  // against on mount, so that 600ms was just added wait before this data
  // ever had a chance to show up.
  useEffect(() => {
    if (draft) scheduleBudgetSync(draft, { immediate: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The draft itself now survives a refresh via useOrgDesignStore's persist
  // middleware, but a closed tab still loses whatever hasn't been synced to
  // the server yet — warn before that happens, same as any editor with
  // unsaved work. Browsers show their own generic message regardless of
  // the text set here.
  useEffect(() => {
    if (!draft) return;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [draft]);

  if (!draft) return null;

  // Excludes a node's own descendants so a role can't be moved under itself.
  // currentManagerId/Label are appended as a disabled entry only when the
  // role's real manager isn't in the options list (e.g. reports outside the
  // fetched roster) — otherwise SbSelect would blank the field out.
  const reparentOptionsFor = (nodeId, currentManagerId, currentManagerLabel) => {
    const excluded = getDescendantIds(pseudoNodesForReparent, nodeId);
    const options = [
      ...draft.roles.filter((r) => !excluded.has(r.sb_unique_id)).map((r) => ({ value: r.sb_unique_id, label: r.job_title })),
      ...draft.newRoles
        .filter((nr) => !excluded.has(newRoleNodeId(nr.id)))
        .map((nr) => ({ value: newRoleNodeId(nr.id), label: `${nr.role_title} (proposed)` })),
    ];
    if (currentManagerId && currentManagerLabel && !options.some((o) => o.value === currentManagerId)) {
      options.push({ value: currentManagerId, label: currentManagerLabel, disabled: true });
    }
    return options;
  };

  // Same resolution the "Reports To" select itself uses to display a value,
  // so the column's filter dropdown lists real names/"No manager" instead of
  // raw manager_id values.
  const resolveManagerLabel = (record) => {
    if (!record.manager_id) return "No manager";
    const role = draft.roles.find((r) => r.sb_unique_id === record.manager_id);
    if (role) return role.job_title;
    const newRole = draft.newRoles.find((nr) => newRoleNodeId(nr.id) === record.manager_id);
    return newRole ? `${newRole.role_title} (proposed)` : record.manager_job_title || "No manager";
  };

  // A fresh Existing Org draft has no proposed side yet — before/after start
  // identical, and showing two "different" cards that are really duplicates
  // reads as broken. Org View only splits into two cards once there's an
  // actual difference (a PAIC/opened-version draft already has one).
  const hasProposedChanges =
    draft.newRoles.length > 0 ||
    Object.values(draft.design || {}).some((d) => d?.status && d.status !== "UNCHANGED");

  // Guards against a double-click/double-Enter on the modal's OK button
  // firing two createOrgRedesignVersion calls before either resolves.
  const handleSaveVersion = async () => {
    if (savingVersion) return;
    setSavingVersion(true);
    try {
      const { entry, serverSynced } = await saveVersion(saveLabel);
      track(EVENTS.VERSION_SAVED, {
        entryOption: draft.entryOption,
        isFirstSave,
        serverSynced: !!serverSynced,
        roleCount: draft.roles.length,
        newRoleCount: draft.newRoles.length,
      });
      setSaveModalOpen(false);
      setSaveLabel("");
      // Only shown alongside a real server sync — saveVersion already surfaces
      // its own warning toast when that fails, and showing "Version saved" on
      // top of it would contradict that warning.
      if (entry && serverSynced) SbNotification.success("Version saved", `Saved as ${entry.label}.`);
    } finally {
      setSavingVersion(false);
    }
  };

  // Generates the Current / Proposed / Summary report and saves it straight
  // to disk — no print dialog, and independent of whether Org View or Table
  // View is open. Mounts the off-screen capture stage, waits for it (and any
  // web fonts) to paint, then hands the fully expanded trees to
  // downloadOrgDesignPdf. Guarded against a double-click the same way
  // handleSaveVersion is.
  const handleDownloadPdf = async () => {
    if (pdfExporting) return;
    track(EVENTS.DOWNLOAD_PDF, { view, entryOption: draft.entryOption, hasProposedChanges });
    setPdfExporting(true);
    try {
      await nextPaint();
      await document.fonts?.ready;
      await downloadOrgDesignPdf({
        draft,
        currentTreeNode: draft.before_tree.ch.length ? pdfCurrentTreeRef.current : null,
        proposedTreeNode: hasProposedChanges && draft.after_tree.ch.length ? pdfProposedTreeRef.current : null,
        canViewBudget,
        hasProposedChanges,
        preparedBy,
      });
    } catch (error) {
      console.error("Error generating Organisation Design PDF:", error);
      SbNotification.error("Download failed", "We couldn't generate the PDF. Please try again.");
    } finally {
      setPdfExporting(false);
    }
  };

  // Proactive, matching the pattern the landing page's entry cards already
  // use — surfaces the real permission state before the user opens the
  // modal, rather than only after they click through it and get a toast.
  const isFirstSave = !draft.orgRedesignId;
  const canSaveVersion = isFirstSave ? canCreateOrgRedesign : canUpdateOrgRedesign;
  // Only a CURRENT ORG draft's first save actually sends this label to the
  // server as org_redesign_name (see saveVersion) — PAIC already has a
  // project name from the PAIC project itself, so its modal copy stays generic.
  const isNamingNewExistingOrgVersion = isFirstSave && draft.entryOption === ENTRY_OPTION.EXISTING_ORG;

  const handleAddRole = () => {
    if (!newRoleDraft.title || !newRoleDraft.managerId) {
      SbNotification.warning("Missing fields", "Title and reporting line are required.");
      return;
    }
    const manager = draft.roles.find((r) => r.sb_unique_id === newRoleDraft.managerId);
    track(EVENTS.PROPOSED_ROLE_ADDED, { headcount: newRoleDraft.headcount });
    addRole({
      title: newRoleDraft.title,
      managerId: newRoleDraft.managerId,
      headcount: newRoleDraft.headcount,
      microtaskFunction: manager?.microtask_function || newRoleDraft.microtaskFunction,
      annualCost: newRoleDraft.annualCost,
    });
    setAddRoleOpen(false);
    setNewRoleDraft({ title: "", managerId: "", headcount: 1, microtaskFunction: "", annualCost: 140000 });
  };

  const roleOptions = draft.roles.map((r) => ({ value: r.sb_unique_id, label: r.job_title }));

  // Wrapped rather than tracked inline: the Role cell fires the same rename
  // from both onBlur and onPressEnter, so one wrapper keeps them in step.
  const handleRenameRole = (record, value) => {
    track(EVENTS.ROLE_RENAMED, { roleId: record.sb_unique_id });
    renameRole(record.sb_unique_id, value);
  };

  const handleSetTargetBudget = (value) => {
    // The figure itself is deliberately not sent — no other module sends
    // money, and the useful signal is that a target was set at all.
    track(EVENTS.TARGET_BUDGET_SET, { cleared: value == null });
    setTargetBudget(value);
  };

  const tableColumns = [
    {
      title: "Role",
      dataIndex: "job_title",
      filters: getFilters(draft.roles, "job_title"),
      onFilter: (value, record) => record.job_title === value,
      render: (title, record) => (
        <SbInput
          size="small"
          variant="borderless"
          defaultValue={title}
          onBlur={(e) => e.target.value !== title && handleRenameRole(record, e.target.value)}
          onPressEnter={(e) => e.target.value !== title && handleRenameRole(record, e.target.value)}
        />
      ),
    },
    {
      title: "Function",
      dataIndex: "microtask_function",
      filters: getFilters(draft.roles, "microtask_function"),
      onFilter: (value, record) => record.microtask_function === value,
    },
    {
      title: "Reports To",
      dataIndex: "manager_id",
      filters: getFilters(draft.roles, "manager_id", resolveManagerLabel),
      onFilter: (value, record) => resolveManagerLabel(record) === value,
      render: (managerId, record) => (
        <SbSelect
          size="small"
          variant="borderless"
          value={managerId || undefined}
          placeholder="No manager"
          allowClear
          popupMatchSelectWidth={220}
          options={reparentOptionsFor(record.sb_unique_id, managerId, record.manager_job_title)}
          onChange={(value) => {
            track(EVENTS.ROLE_REPARENTED, { roleId: record.sb_unique_id, cleared: !value });
            updateReportingLine(record.sb_unique_id, value || null);
          }}
        />
      ),
    },
    {
      title: "Current Positions",
      dataIndex: "total_positions",
    },
    {
      title: "Classification",
      key: "status",
      // Read-only: classification is derived from the Target Positions edit
      // (see updateHeadcount), not a user choice — same getRoleChangeConfig
      // lookup that drives the tree nodes and legend renders it here too.
      render: (_, record) => {
        const status = draft.design[record.sb_unique_id]?.status || "UNCHANGED";
        const { label, color } = getRoleChangeConfig(status, draft.entryOption);
        return <SbTag color={color}>{label}</SbTag>;
      },
    },
    {
      title: "Target Positions",
      key: "afterHc",
      render: (_, record) => {
        const design = draft.design[record.sb_unique_id];
        const afterHc = design?.after_hc ?? record.total_positions;
        return (
          <SizedInputNumberStyled
            $width={90}
            size="small"
            min={0}
            value={afterHc}
            onChange={(value) => updateHeadcount(record.sb_unique_id, value)}
            onBlur={() => {
              const committed = draft.design[record.sb_unique_id]?.after_hc;
              if (committed != null && committed !== record.total_positions) {
                track(EVENTS.ROLE_HEADCOUNT_CHANGED, {
                  roleId: record.sb_unique_id,
                  before: record.total_positions,
                  after: committed,
                });
              }
            }}
          />
        );
      },
    },
  ];

  const newRoleColumns = [
    { title: "Role", dataIndex: "role_title" },
    { title: "Function", dataIndex: "microtask_function" },
    {
      title: "Reports To",
      dataIndex: "manager_id",
      render: (managerId, record) => (
        <SbSelect
          size="small"
          variant="borderless"
          value={managerId || undefined}
          placeholder="No manager"
          allowClear
          popupMatchSelectWidth={false}
          options={reparentOptionsFor(newRoleNodeId(record.id))}
          onChange={(value) => updateNewRoleManager(record.id, value || null)}
        />
      ),
    },
    // A proposed role never existed "before", so Current Positions is
    // always 0 and Status is always Created (not an editable dropdown).
    { title: "Current Positions", key: "currentPositions", render: () => 0 },
    {
      title: "Classification",
      key: "status",
      render: () => <SbTag color={STATUS_COLORS.CREATED}>{STATUS_LABELS.CREATED}</SbTag>,
    },
    {
      title: "Target Positions",
      dataIndex: "after_hc",
      render: (hc, record) => (
        <SizedInputNumberStyled
          $width={90}
          size="small"
          min={1}
          value={hc}
          onChange={(value) => updateNewRoleHeadcount(record.id, value)}
        />
      ),
    },
    {
      title: "",
      key: "actions",
      align: "right",
      render: (_, record) => (
        <SbButton
          buttonType="action"
          icon={<DeleteOutlined />}
          onClick={() =>
            showSbConfirm({
              title: "Delete this proposed role?",
              content: `"${record.role_title}" will be removed from this Organisation Design. This can't be undone.`,
              okText: "Delete",
              cancelText: "Cancel",
              okButtonProps: { danger: true },
              onOk: () => {
                track(EVENTS.PROPOSED_ROLE_REMOVED, { roleId: record.id });
                removeNewRole(record.id);
              },
            })
          }
        />
      ),
    },
  ];

  return (
    <PageTransitionStyled>
      <PageHeaderStyled>
        <HeaderLeftStyled>
          <BackLinkStyled onClick={() => navigate("/org-design")} {...activatableRowProps(() => navigate("/org-design"))}>
            <ArrowLeftOutlined /> Back to Organisation Design
          </BackLinkStyled>
          <HeadingRowStyled>
            <Title level={4}>Organisation Design Workspace</Title>
          </HeadingRowStyled>
          {draft.sourceLabel && <Text type="secondary">{draft.sourceLabel}</Text>}
        </HeaderLeftStyled>
      </PageHeaderStyled>

      <WorkspaceToolbarStyled>
        <Radio.Group
          value={view}
          onChange={(e) => {
            track(EVENTS.VIEW_TOGGLED, { view: e.target.value });
            setView(e.target.value);
          }}
          options={[{ label: "Table View", value: VIEW_TABLE }, { label: "Org View", value: VIEW_ORG }]}
          optionType="button"
        />

        <ToolbarActionsStyled>
          {/* Held while a budget/role-count sync is in flight — the PDF's
              Summary page would otherwise capture stale or not-yet-loaded
              figures that the on-screen Budget Bar is itself hiding. */}
          <SbTooltip content="Updating figures — the PDF will be available in a moment." disabled={!budgetSyncing || pdfExporting}>
            <TooltipTargetStyled>
              <SbButton
                buttonType="secondary"
                icon={<DownloadOutlined />}
                onClick={handleDownloadPdf}
                loading={pdfExporting}
                disabled={budgetSyncing}
              >
                Download PDF
              </SbButton>
            </TooltipTargetStyled>
          </SbTooltip>
          <SbTooltip content="You don't have permission to save this Organisation Design version." disabled={canSaveVersion}>
            <TooltipTargetStyled>
              <SbButton buttonType="secondary" icon={<SaveOutlined />} onClick={() => setSaveModalOpen(true)} disabled={!canSaveVersion}>
                Save Version
              </SbButton>
            </TooltipTargetStyled>
          </SbTooltip>
        </ToolbarActionsStyled>
      </WorkspaceToolbarStyled>

      {view === VIEW_ORG && <RoleChangeLegend entryOption={draft.entryOption} />}

      {view === VIEW_ORG && hasProposedChanges && (
        <OrgTreeCardsRowStyled>
          <OrgTreeCard
            title="Current"
            variant="current"
            rootChildren={draft.before_tree.ch}
            exportFilename="org-design-current"
            entryOption={draft.entryOption}
            {...treeViewport}
          />
          <TreeDividerStyled />
          <OrgTreeCard
            title="Proposed"
            variant="proposed"
            rootChildren={draft.after_tree.ch}
            exportFilename="org-design-proposed"
            entryOption={draft.entryOption}
            {...treeViewport}
          />
        </OrgTreeCardsRowStyled>
      )}

      {view === VIEW_ORG && !hasProposedChanges && (
        <OrgTreeCardsRowStyled>
          <OrgTreeCard
            title="Org Structure"
            variant="current"
            rootChildren={draft.before_tree.ch}
            exportFilename="org-design-current"
            entryOption={draft.entryOption}
            {...treeViewport}
          />
        </OrgTreeCardsRowStyled>
      )}

      {view === VIEW_TABLE && (
        <RoleTableWrapperStyled>
          <ProposedRolesHeaderStyled>
            <Text strong>Proposed / Added Roles</Text>
            <SbTooltip content="You don't have permission to add a role without compensation data." disabled={canViewBudget}>
              <TooltipTargetStyled>
                <SbButton buttonType="primary" size="small" icon={<PlusOutlined />} onClick={() => setAddRoleOpen(true)} disabled={!canViewBudget}>
                  Add Role
                </SbButton>
              </TooltipTargetStyled>
            </SbTooltip>
          </ProposedRolesHeaderStyled>
          {draft.newRoles.length > 0 && (
            <SbTable columns={newRoleColumns} dataSource={draft.newRoles} rowKey="id" pagination={false} size="small" />
          )}

          <ProposedRolesHeaderStyled>
            <Text strong>Roles</Text>
          </ProposedRolesHeaderStyled>
          <SbTable columns={tableColumns} dataSource={draft.roles} rowKey="sb_unique_id" pagination={false} size="small" />
        </RoleTableWrapperStyled>
      )}

      {canViewBudget && (
        <OrgDesignBudgetBar
          baselineBudget={draft.baseline_budget}
          targetBudget={draft.targetBudget}
          projectedBudget={draft.projected_budget}
          onSetTargetBudget={handleSetTargetBudget}
          syncing={budgetSyncing}
        />
      )}
      <OrgDesignSummaryBridge
        stats={draft.stats}
        statusCounts={draft.status_counts}
        roleCounts={draft.roleCounts}
        entryOption={draft.entryOption}
      />

      {/* Off-screen, fully expanded Current/Proposed trees for the PDF to
          capture (see PdfCaptureStageStyled / utils/orgDesignPdf.jsx) —
          mounted only while a download is being generated. */}
      {pdfExporting && (
        <PdfCaptureStageStyled>
          <TreeContainerStyled ref={pdfCurrentTreeRef}>
            <OrgChartListStyled>
              {draft.before_tree.ch.map((node) => (
                <OrgDesignChartNode key={node.id} node={node} editable={false} onEdit={() => {}} entryOption={draft.entryOption} />
              ))}
            </OrgChartListStyled>
          </TreeContainerStyled>
          {/* Only needed when the PDF gets a separate Proposed page. */}
          {hasProposedChanges && (
            <TreeContainerStyled ref={pdfProposedTreeRef}>
              <OrgChartListStyled>
                {draft.after_tree.ch.map((node) => (
                  <OrgDesignChartNode key={node.id} node={node} editable={false} onEdit={() => {}} entryOption={draft.entryOption} />
                ))}
              </OrgChartListStyled>
            </TreeContainerStyled>
          )}
        </PdfCaptureStageStyled>
      )}

      {/* ── Save Version ── */}
      <SbModal
        open={saveModalOpen}
        title="Save Version"
        onCancel={() => setSaveModalOpen(false)}
        onOk={handleSaveVersion}
        okText="Save"
        confirmLoading={savingVersion}
      >
        <SbInput
          placeholder={isNamingNewExistingOrgVersion ? "Project name" : "Version label (optional)"}
          value={saveLabel}
          onChange={(e) => setSaveLabel(e.target.value)}
        />
      </SbModal>

      {/* ── Add Role ── */}
      <SbModal open={addRoleOpen} title="Add Proposed Role" onCancel={() => setAddRoleOpen(false)} onOk={handleAddRole} okText="Add">
        <ModalFieldStyled>
          <SbInput
            placeholder="Role title"
            value={newRoleDraft.title}
            onChange={(e) => setNewRoleDraft((d) => ({ ...d, title: e.target.value }))}
          />
        </ModalFieldStyled>
        <ModalFieldStyled>
          <SbSelect
            placeholder="Reports to"
            options={roleOptions}
            value={newRoleDraft.managerId || undefined}
            onChange={(value) => setNewRoleDraft((d) => ({ ...d, managerId: value }))}
          />
        </ModalFieldStyled>
        <ModalFieldStyled>
          <FullWidthInputNumberStyled
            min={1}
            value={newRoleDraft.headcount}
            onChange={(value) => setNewRoleDraft((d) => ({ ...d, headcount: value }))}
            addonBefore="Headcount"
          />
        </ModalFieldStyled>
        {canViewBudget && (
        <FullWidthInputNumberStyled
          min={0}
          step={5000}
          value={newRoleDraft.annualCost}
          onChange={(value) => setNewRoleDraft((d) => ({ ...d, annualCost: value }))}
          addonBefore="Budget (Annual $/FTE)"
          formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
          parser={(value) => value.replace(/,*/g, "")}
        />
        )}
      </SbModal>
    </PageTransitionStyled>
  );
};

export default OrgDesignWorkspace;
